'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Pause, Play, X, Volume2, VolumeX, MessageCircle, Sparkles, Pencil, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { authService } from '@/lib/authService';
import { useGuidedTour } from './GuidedTourProvider';
import { isTourVideoCaptureMode } from '@/lib/tourCaptureMode';
import { useTourVerification, GUIDED_TOUR_DISMISS_REVEAL_EVENT } from './hooks/useTourVerification';
import { useTourMascot } from './hooks/useTourMascot';
import { useTourTTS } from './hooks/useTourTTS';
import { useTourAI } from './hooks/useTourAI';
import { useTourSpotlightContext } from './TourSpotlightContext';
import type { SpotlightRect } from './hooks/useTourSpotlight';
import { getPoseNaturalDirection, resolveFlipStyle } from './utils/mascotChoreography';
import { enterTrainingSandbox } from './utils/enterTrainingSandbox';
import { isTourMediaRegenAvailable, requestTourMediaRegen } from './utils/tourMediaRegen';
import { StepHintGenerator } from './ai/StepHintGenerator';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { uiViewportHeight, uiViewportWidth } from '@/lib/uiScale';

type MascotPanelSide = 'left' | 'right';
type MobileCardAnchor = 'above' | 'below';

/**
 * Bottom edge of the usable page area for fixed tour chrome — the top of any
 * `[data-tour-bottom-chrome]` element (ClientZoneFooter strip, Capacitor
 * mobile footer) rendered by the app shell, else the window edge. Fixed
 * cards would otherwise cover that chrome and fall off the usable area.
 */
function tourViewportBottom(): number {
  if (typeof window === 'undefined') return 0;
  let bottom = uiViewportHeight();
  // Marked bottom chrome wins outright; bare <footer> elements only count
  // when their bottom edge reaches the window bottom (the app-shell strip —
  // in-page article footers mid-document would have a smaller bottom).
  for (const el of document.querySelectorAll<HTMLElement>(
    '[data-tour-bottom-chrome], footer'
  )) {
    const r = el.getBoundingClientRect();
    if (r.top <= 0 || r.top >= bottom) continue;
    const marked = el.hasAttribute('data-tour-bottom-chrome');
    if (marked || r.bottom >= uiViewportHeight() - 4) {
      bottom = r.top;
    }
  }
  return bottom;
}

function resolveTourLayout(
  spotlightRect: SpotlightRect | null,
  panelSide: MascotPanelSide,
  mobileCardAnchor: MobileCardAnchor,
  showQAPanel: boolean = false
): { cardStyle: CSSProperties; mascotStyle: CSSProperties } | null {
  if (!spotlightRect || typeof window === 'undefined') {
    return null;
  }

  const viewportW = uiViewportWidth();
  // Bound to the usable area — a bottom chrome strip (client-zone footer)
  // sits below this; fixed cards must stop above it.
  const viewportH = tourViewportBottom();
  const isCompact = window.innerWidth < 640;
  const pad = 16;
  const gap = 16;

  const cardW = Math.min(viewportW - pad * 2, isCompact ? 320 : 384);
  // Give the card enough vertical room when the Ask AI panel is open,
  // but never let it exceed the viewport.
  const baseCardH = isCompact ? 360 : 440;
  const qaExtra = showQAPanel ? 160 : 0;
  const cardH = Math.min(viewportH - pad * 2, baseCardH + qaExtra);
  const mascotSize = isCompact ? 80 : 128;

  let cardLeft = 0;
  let cardTop = 0;
  let mascotLeft = 0;
  let mascotTop = 0;

  const targetMidY = spotlightRect.top + spotlightRect.height / 2;

  if (isCompact) {
    cardLeft = (viewportW - cardW) / 2;
    const anchor = mobileCardAnchor === 'below' ? 'below' : 'above';

    if (anchor === 'below') {
      cardTop = Math.min(
        viewportH - cardH - pad,
        Math.max(pad, spotlightRect.bottom + gap + mascotSize + gap)
      );
      mascotTop = Math.max(pad, cardTop - mascotSize - gap);
    } else {
      cardTop = Math.max(pad, Math.min(viewportH - cardH - pad, spotlightRect.top - gap - mascotSize - gap - cardH));
      mascotTop = cardTop + cardH + gap;
    }

    mascotLeft = (viewportW - mascotSize) / 2;
  } else {
    const spaceRight = viewportW - spotlightRect.right - pad;
    const spaceLeft = spotlightRect.left - pad;
    const neededW = cardW + gap + mascotSize + gap;

    let side: MascotPanelSide = panelSide;
    if (side === 'right' && spaceRight < neededW && spaceLeft >= neededW) {
      side = 'left';
    } else if (side === 'left' && spaceLeft < neededW && spaceRight >= neededW) {
      side = 'right';
    }

    if (side === 'right') {
      mascotLeft = Math.min(viewportW - mascotSize - pad, Math.max(pad, spotlightRect.right + gap));
      cardLeft = Math.min(viewportW - cardW - pad, mascotLeft + mascotSize + gap);

      if (cardLeft < mascotLeft + mascotSize + gap) {
        cardLeft = Math.max(pad, viewportW - cardW - pad);
        mascotLeft = Math.max(pad, cardLeft - mascotSize - gap);
      }
    } else {
      mascotLeft = Math.max(pad, spotlightRect.left - gap - mascotSize);
      cardLeft = Math.max(pad, mascotLeft - gap - cardW);

      if (cardLeft > mascotLeft - gap - cardW) {
        cardLeft = pad;
        mascotLeft = Math.min(viewportW - mascotSize - pad, cardLeft + cardW + gap);
      }
    }

    const cardTopMax = viewportH - cardH - pad;
    cardTop = Math.max(pad, Math.min(targetMidY - cardH / 2, cardTopMax));

    const mascotTopMax = viewportH - mascotSize - pad;
    mascotTop = Math.max(pad, Math.min(targetMidY - mascotSize / 2, mascotTopMax));
  }

  return {
    cardStyle: { left: Math.round(cardLeft), top: Math.round(cardTop) },
    mascotStyle: { left: Math.round(mascotLeft), top: Math.round(mascotTop) },
  };
}

export function GuidedTourRunner() {
  const { isOpen, engine, progress, schema, currentStep, config, moduleKey, role, selectedObjective } = useGuidedTour();
  const { spotlightRect, focusTargetState } = useTourSpotlightContext();
  const [showQAPanel, setShowQAPanel] = useState(false);
  const [qaInput, setQaInput] = useState('');
  const [qaResponse, setQaResponse] = useState<string | null>(null);
  const [qaQuestionTimestamp, setQaQuestionTimestamp] = useState<number | null>(null);
  const [hintShownForStep, setHintShownForStep] = useState<string | null>(null);

  // ── Inline editor (dev + root) ────────────────────────────────────────
  // Edits the CURRENT step's text (or the flow's intro text) right on the
  // running tour card — mascot and spotlight stay live on the real page so
  // the author sees exactly what a learner sees. Saves go through the
  // scoped PATCH endpoint (server-side read-modify-write of the module
  // YAML + registry reload), after which config.refetch() re-renders the
  // card with fresh text. "Save + regenerate audio" re-synthesizes just
  // this step's Kokoro WAVs — never the whole flow.
  const editAvailable = isTourMediaRegenAvailable();
  const [editMode, setEditMode] = useState<'step' | 'flow' | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, string>>({});
  const [editSaving, setEditSaving] = useState(false);

  const trackEvent = useCallback((event: {
    event_type: string;
    flow_id?: string;
    step_id?: string;
    step_index?: number;
    time_spent_seconds?: number;
  }) => {
    fetch('/api/v1/training/tour-analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...event,
        tour_key: moduleKey,
      }),
    }).catch(() => {});
  }, [moduleKey]);

  const tourAI = useTourAI({ enabled: isOpen && engine.viewMode === 'runner' });

  const { isVerified, canAdvance } = useTourVerification({
    step: currentStep,
    stepId: currentStep?.id ?? null,
    verifiedStepIds: progress.progress.verified_step_ids,
    onVerify: progress.markStepVerified,
    enabled: isOpen && engine.viewMode === 'runner',
    consumeDomEvidenceSuppression: engine.consumeDomEvidenceSuppression,
  });

  const verificationRequired = useMemo(() => {
    if (!currentStep?.verification) return false;
    return !!(currentStep.verification.event_type || currentStep.verification.target_selector);
  }, [currentStep?.verification]);

  // While the step editor is open, feed the draft mascot_pose/position into
  // the choreography so the mascot jumps to the new pose/dock live — the
  // author sees exactly what learners will see before saving.
  const mascotPreviewStep = useMemo(() => {
    if (editMode === 'step' && currentStep) {
      return {
        ...currentStep,
        mascot_pose: editDraft.mascot_pose ?? currentStep.mascot_pose,
        mascot_position: editDraft.mascot_position ?? currentStep.mascot_position,
      };
    }
    return currentStep;
  }, [editMode, currentStep, editDraft.mascot_pose, editDraft.mascot_position]);

  const { choreography, currentPoseAsset, defaultPoseUrl, assets } = useTourMascot({
    currentStep: mascotPreviewStep,
    currentStepIndex: engine.currentStepIndex,
    isOpen,
    isIndexMode: false,
    mascotConfig: schema?.mascot ?? null,
  });

  const narrationText = currentStep
    ? (currentStep.scripted_description || `${currentStep.title}. ${currentStep.summary}`)
    : '';

  // ── Flow intro phase ──────────────────────────────────────────────────
  // Before the first operational step the runner shows a welcome/objectives
  // card ("Welcome — this tour focuses on …, here's what you'll be able to
  // do — ready?"). The flow's pre-generated summary narration plays over it.
  // Dismissed intros are remembered per flow for the lifetime of this
  // mounted runner so Back-navigation and flow chaining behave sanely.
  // Skipped entirely in video-capture mode (the MP4 format has its own
  // opening card) and when a flow was opened directly at a later step.
  const introDismissedRef = useRef<Set<string>>(new Set());
  const [, setIntroDismissedTick] = useState(0);
  const captureMode = isTourVideoCaptureMode();
  const showIntro = Boolean(
    isOpen &&
      engine.viewMode === 'runner' &&
      engine.currentFormat === 'interactive' &&
      !engine.selectedObjectiveId &&
      engine.currentStepIndex === 0 &&
      engine.selectedFlow &&
      !introDismissedRef.current.has(engine.selectedFlow.id) &&
      !captureMode,
  );



  // Compute next step for audio prefetching — allows the next step's
  // pre-generated audio to be fetched in the background while the current
  // step plays.
  const nextStep = useMemo(() => {
    if (!currentStep || engine.currentStepIndex >= engine.currentSteps.length - 1) return null;
    return engine.currentSteps[engine.currentStepIndex + 1] ?? null;
  }, [currentStep, engine.currentStepIndex, engine.currentSteps]);

  const {
    enabled: ttsEnabled,
    toggleEnabled: toggleTts,
    isPlaying: ttsIsPlaying,
    isLoading: ttsIsLoading,
    isPaused: ttsIsPaused,
    play: ttsPlay,
    pause: ttsPause,
    resume: ttsResume,
    playUrl: ttsPlayUrl,
    stop: ttsStop,
  } = useTourTTS({
    currentStepId: currentStep?.id ?? null,
    currentNarrationText: narrationText,
    currentAudioUrl: currentStep?.audio_url ?? null,
    currentTitleAudioUrl: currentStep?.title_audio_url ?? null,
    nextStepId: nextStep?.id ?? null,
    nextAudioUrl: nextStep?.audio_url ?? null,
    nextTitleAudioUrl: nextStep?.title_audio_url ?? null,
    voice: currentStep?.tts_voice ?? schema?.tts?.voice ?? schema?.audio?.default_voice ?? 'af_heart',
    // Treat leaving runner mode (index/chat) — or switching to a non-
    // interactive format — like closing: stop narration so it doesn't play
    // underneath the video/quick-guide (the video embeds its own audio).
    // Also gated while the flow intro card shows so step-1 narration doesn't
    // start underneath it — and while the inline editor is open so the
    // author can edit in silence (the edit gate also kills in-flight audio
    // via the hook's visibility effect).
    isOpen:
      isOpen && engine.viewMode === 'runner' && engine.currentFormat === 'interactive' && !showIntro && !editMode,
    isMinimized: engine.isMinimized,
    autoPlay: schema?.audio?.auto_play ?? false,
  });

  // Flow intro narration — play the flow's pre-generated summary audio over
  // the welcome card (same WAV the welcome overlay uses; hash-verified
  // against view_summary server-side).
  const introAutoPlayedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showIntro || !engine.selectedFlow) return;
    if (introAutoPlayedRef.current === engine.selectedFlow.id) return;
    introAutoPlayedRef.current = engine.selectedFlow.id;
    if (editMode) return;
    if (!(schema?.audio?.auto_play ?? false)) return;
    const url = engine.selectedFlow.summary_audio_url;
    if (url) void ttsPlayUrl(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIntro, engine.selectedFlow?.id, editMode]);

  const dismissIntro = useCallback(() => {
    if (engine.selectedFlow) {
      introDismissedRef.current.add(engine.selectedFlow.id);
      setIntroDismissedTick((t) => t + 1);
    }
    // Stop the intro's summary narration so it doesn't overlap the first
    // step's audio, which auto-plays as soon as the intro gate lifts.
    ttsStop();
  }, [engine.selectedFlow, ttsStop]);

  // ── Inline edit handlers ──────────────────────────────────────────────
  // Exiting edit mode when the step/flow changes under us keeps a stale
  // draft from writing back onto a different step.
  useEffect(() => {
    setEditMode(null);
  }, [currentStep?.id, engine.selectedFlowId, showIntro]);

  const openStepEditor = useCallback(() => {
    if (!currentStep) return;
    ttsStop();
    setEditDraft({
      eyebrow: currentStep.eyebrow ?? '',
      title: currentStep.title ?? '',
      summary: currentStep.summary ?? '',
      scripted_description: currentStep.scripted_description ?? '',
      bullets: (currentStep.bullets ?? []).join('\n'),
      focus_target: currentStep.focus_target ?? '',
      tts_voice: currentStep.tts_voice ?? '',
      mascot_pose: currentStep.mascot_pose ?? '',
      mascot_position: currentStep.mascot_position ?? '',
    });
    setEditMode('step');
  }, [currentStep, ttsStop]);

  const openFlowEditor = useCallback(() => {
    const flow = engine.selectedFlow;
    if (!flow) return;
    ttsStop();
    setEditDraft({
      label: flow.label ?? '',
      view_summary: flow.view_summary ?? '',
      summary: flow.summary ?? '',
      objectives: (flow.objectives ?? []).join('\n'),
    });
    setEditMode('flow');
  }, [engine.selectedFlow, ttsStop]);

  // Edit-intent flag set by the tour index's per-flow pencil ('flow') or the
  // media manager's per-step pencil ('step'): the tour launched specifically
  // to be edited, so open the matching editor as soon as the right card
  // renders. Consumed once — for a step-1 deep link we first dismiss the
  // welcome card, then reopen on the next pass once the step is showing.
  useEffect(() => {
    if (!editAvailable || editMode || !isOpen || engine.viewMode !== 'runner') return;
    let intent: string | null = null;
    try {
      intent = sessionStorage.getItem('guided-tour:edit-intent');
    } catch { /* sessionStorage unavailable — skip auto-open */ }
    if (intent === 'flow') {
      if (!showIntro) return;
      sessionStorage.removeItem('guided-tour:edit-intent');
      openFlowEditor();
    } else if (intent === 'step') {
      if (showIntro) {
        if (engine.selectedFlow) {
          introDismissedRef.current.add(engine.selectedFlow.id);
          setIntroDismissedTick((t) => t + 1);
        }
        return; // re-runs once the step card is actually showing
      }
      if (!currentStep) return;
      sessionStorage.removeItem('guided-tour:edit-intent');
      openStepEditor();
    }
  }, [editAvailable, editMode, isOpen, engine.viewMode, showIntro, currentStep, engine.selectedFlow, openFlowEditor, openStepEditor]);

  const saveEdit = useCallback(async (regenAudio: boolean) => {
    const flowId = engine.selectedFlow?.id;
    if (!flowId || !editMode) return;
    setEditSaving(true);
    try {
      const trimOrNull = (v?: string) => { const t = (v ?? '').trim(); return t ? t : null; };
      const linesOrNull = (v?: string) => {
        const lines = (v ?? '').split('\n').map((s) => s.trim()).filter(Boolean);
        return lines.length ? lines : null;
      };
      const patch = editMode === 'step'
        ? {
            eyebrow: trimOrNull(editDraft.eyebrow),
            title: trimOrNull(editDraft.title),
            summary: trimOrNull(editDraft.summary),
            scripted_description: trimOrNull(editDraft.scripted_description),
            bullets: linesOrNull(editDraft.bullets),
            focus_target: trimOrNull(editDraft.focus_target),
            tts_voice: trimOrNull(editDraft.tts_voice),
            mascot_pose: trimOrNull(editDraft.mascot_pose),
            mascot_position: trimOrNull(editDraft.mascot_position),
          }
        : {
            label: trimOrNull(editDraft.label),
            view_summary: trimOrNull(editDraft.view_summary),
            summary: trimOrNull(editDraft.summary),
            objectives: linesOrNull(editDraft.objectives),
          };

      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/guided-tours/modules/${moduleKey}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          flowId,
          ...(editMode === 'step' && currentStep ? { stepId: currentStep.id } : {}),
          patch,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.error ?? `HTTP ${res.status}`);
      }

      if (regenAudio) {
        // '__summary__' is the worker's synthetic step id for the flow's
        // welcome-card narration — scoping to it regenerates only that WAV
        // (plus its title entry when editing a step).
        const regen = await requestTourMediaRegen({
          moduleKey,
          flowId,
          stepIds: editMode === 'step' && currentStep ? [currentStep.id] : ['__summary__'],
          kind: 'audio',
        });
        if (regen.success) {
          toast.success("Saved — regenerating this step's audio");
        } else {
          toast.success('Saved');
          toast.error(regen.error ?? 'Audio regeneration failed to queue');
        }
      } else {
        toast.success('Saved — tour YAML updated');
      }

      setEditMode(null);
      // Pull the freshly reloaded config so this card (and the attached
      // audio URLs) reflect the write immediately.
      config.refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Save failed');
    } finally {
      setEditSaving(false);
    }
  }, [editMode, editDraft, engine.selectedFlow?.id, currentStep, moduleKey, config]);

  // Re-synthesize the current card's audio without editing — the welcome
  // card's '__summary__' WAV while the intro shows, otherwise this step's
  // narration + title pair. Queues on the BullMQ general queue; the Media
  // manager (Guided Tours → Media) shows progress and the result.
  const [audioRegenBusy, setAudioRegenBusy] = useState(false);
  const regenCurrentAudio = useCallback(async () => {
    const flowId = engine.selectedFlow?.id;
    if (!flowId || audioRegenBusy) return;
    setAudioRegenBusy(true);
    try {
      const res = await requestTourMediaRegen({
        moduleKey,
        flowId,
        stepIds: showIntro || !currentStep ? ['__summary__'] : [currentStep.id],
        kind: 'audio',
      });
      if (res.success) {
        toast.success(res.message ?? 'Audio regeneration queued');
      } else {
        toast.error(res.error ?? 'Audio regeneration failed to queue');
      }
    } finally {
      setAudioRegenBusy(false);
    }
  }, [engine.selectedFlow?.id, audioRegenBusy, moduleKey, showIntro, currentStep]);

  // Re-bound on scroll/resize: bottom chrome (client-zone footer) moves
  // relative to the viewport when the page scrolls, and nothing else here
  // observes it — steps without a spotlight would keep a stale bound.
  const [viewportTick, setViewportTick] = useState(0);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setViewportTick((t) => t + 1));
    };
    window.addEventListener('resize', tick);
    window.addEventListener('scroll', tick, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', tick);
      window.removeEventListener('scroll', tick, { capture: true });
    };
  }, []);

  const layout = useMemo(
    () => resolveTourLayout(spotlightRect, choreography.panelSide, choreography.mobileCardAnchor, showQAPanel),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- viewportTick re-derives bounds when chrome moves
    [spotlightRect, choreography.panelSide, choreography.mobileCardAnchor, showQAPanel, viewportTick]
  );

  // When a reveal-modal step has no spotlight (the modal is open but we're not
  // drawing a ring around it), position the card at the TOP-center of the
  // viewport so it doesn't cover the modal's action buttons at the bottom.
  const revealModalLayout = useMemo<{ cardStyle: CSSProperties; mascotStyle: CSSProperties } | null>(() => {
    if (layout || !currentStep?.reveal_target || typeof window === 'undefined') {
      return null;
    }

    const viewportW = uiViewportWidth();
    const isCompact = window.innerWidth < 640;
    const pad = 16;
    const gap = 16;
    const cardW = Math.min(viewportW - pad * 2, isCompact ? 320 : 384);
    const cardH = Math.min(tourViewportBottom() - pad * 2, isCompact ? 360 : 420);
    const mascotSize = isCompact ? 80 : 128;

    const cardLeft = (viewportW - cardW) / 2;
    // Position at the top so the card doesn't cover modal buttons at the bottom
    const cardTop = pad;
    const mascotLeft = Math.max(pad, cardLeft - mascotSize - gap);
    const mascotTop = cardTop + (cardH - mascotSize) / 2;

    return {
      cardStyle: { left: Math.round(cardLeft), top: Math.round(cardTop) },
      mascotStyle: { left: Math.round(mascotLeft), top: Math.round(mascotTop) },
    };
  }, [layout, currentStep?.reveal_target]);

  // When there is no spotlight and no reveal modal (the majority of
  // conceptual/intro steps that don't point at a specific DOM element),
  // position the card and mascot side-by-side at the viewport's vertical
  // center. Without this, the CSS fallback classes pin the card to the
  // top/bottom edge and the mascot to the vertical middle, leaving them on
  // opposite ends of the screen.
  const centerFallbackLayout = useMemo<{
    cardStyle: CSSProperties;
    mascotStyle: CSSProperties;
    mascotFaces: 'left' | 'right';
  } | null>(() => {
    if (layout || revealModalLayout || typeof window === 'undefined') {
      return null;
    }

    const viewportW = uiViewportWidth();
    const viewportH = tourViewportBottom();
    const isCompact = window.innerWidth < 640;

    // On mobile the CSS fallback already stacks the card and mascot
    // vertically (card at top/bottom, mascot on the opposite edge). That
    // is acceptable on small screens, so only apply the center fallback on
    // desktop where the horizontal side-by-side arrangement is expected.
    if (isCompact) {
      return null;
    }

    const pad = 16;
    const gap = 16;
    const cardW = Math.min(viewportW - pad * 2, 384);
    // Narration cards render ~300–380px tall — well under the 440px cap.
    // Centering on a realistic height keeps the mascot level with the
    // card body; the 440 estimate left it floating below the card.
    const cardH = Math.min(viewportH - pad * 2, 380);
    const mascotSize = 128;

    const targetMidY = viewportH / 2;
    const targetCenterX = viewportW / 2;

    const side: MascotPanelSide = choreography.panelSide;

    // Treat card + mascot as one centered group so the pair sits on the
    // target midline — previously the mascot anchored at center and the
    // card hung off it, leaving both ~250px off-centre and detached.
    const groupW = mascotSize + gap + cardW;
    const groupLeft = Math.max(
      pad,
      Math.min(targetCenterX - groupW / 2, viewportW - groupW - pad),
    );

    // panelSide says which side the card (panel) sits on relative to the
    // mascot, so the pose direction still points at the card.
    const cardLeft = side === 'left' ? groupLeft : groupLeft + mascotSize + gap;
    const mascotLeft = side === 'left' ? groupLeft + cardW + gap : groupLeft;

    const cardTop = Math.max(pad, Math.min(targetMidY - cardH / 2, viewportH - cardH - pad));
    const mascotTop = cardTop + cardH / 2 - mascotSize / 2;

    return {
      cardStyle: { left: Math.round(cardLeft), top: Math.round(cardTop) },
      mascotStyle: { left: Math.round(mascotLeft), top: Math.round(mascotTop) },
      // The mascot must face AWAY from the card — outward into the page
      // content the card describes. panelSide is the card's side relative to
      // the mascot, so the mascot gestures the opposite way, matching the
      // spotlight layout where the mascot always sits between the card and
      // the target and points at the target. (Pointing back at its own
      // narration card reads as "look at the card", which is backwards.)
      mascotFaces: side === 'left' ? 'right' : 'left',
    };
  }, [layout, revealModalLayout, choreography.panelSide]);

  const effectiveLayout = layout ?? revealModalLayout ?? centerFallbackLayout;

  // Whatever layout produced the card's top edge, the card must never extend
  // past the viewport's bottom — the layout math budgets an assumed card
  // height (~380–440px), but edit mode can be much taller. Cap maxHeight on
  // the positioned card so CardContent's overflow-y-auto actually has a box
  // to scroll inside instead of spilling off-screen.
  const boundedCardStyle = useMemo<CSSProperties | undefined>(() => {
    if (!effectiveLayout) return undefined;
    const top =
      typeof effectiveLayout.cardStyle.top === 'number'
        ? effectiveLayout.cardStyle.top
        : 16;
    const style: CSSProperties = {
      ...effectiveLayout.cardStyle,
      maxHeight: Math.max(240, Math.floor(tourViewportBottom() - top - 16)),
    };
    // Edit mode packs many fields — widen the card so it needs less vertical
    // room, then re-clamp the left edge so the wider card can't spill off
    // the right side (the layout math budgeted the standard 384px width).
    if (editMode) {
      const width = Math.min(uiViewportWidth() - 32, 544);
      style.width = width;
      if (typeof style.left === 'number') {
        style.left = Math.max(16, Math.min(style.left, uiViewportWidth() - width - 16));
      }
    }
    return style;
  }, [effectiveLayout, editMode]);

  // Pure-CSS fallback (no spotlight/fallback layout — compact screens): the
  // anchor classes pin to the window edge, so inject the chrome-aware bound
  // inline. Bottom-anchored cards get their `bottom` lifted above the footer
  // strip; top-anchored cards just get a shorter max-height.
  const cssBoundCardStyle = useMemo<CSSProperties | undefined>(() => {
    if (effectiveLayout || typeof window === 'undefined') return undefined;
    const chromeInset = uiViewportHeight() - tourViewportBottom();
    const style: CSSProperties = {
      maxHeight:
        choreography.mobileCardAnchor === 'below'
          ? Math.max(240, uiViewportHeight() - chromeInset - 32)
          : Math.max(240, uiViewportHeight() - chromeInset - 96),
    };
    if (choreography.mobileCardAnchor === 'below') {
      // `bottom-4` anchor — lift the card above the bottom chrome strip.
      style.bottom = chromeInset + 16;
    }
    if (editMode) {
      style.width = Math.min(uiViewportWidth() - 32, 544);
    }
    return style;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- viewportTick re-derives bounds when chrome moves
  }, [effectiveLayout, choreography.mobileCardAnchor, editMode, viewportTick]);

  const cardChromeStyle = boundedCardStyle ?? cssBoundCardStyle;

  // Compute an additional flip so the mascot always faces the actual target,
  // regardless of what the dock-position heuristic decided.
  const targetAwareFlipStyle = useMemo<CSSProperties | undefined>(() => {
    if (!spotlightRect || !effectiveLayout) {
      // No spotlight: the center-fallback layout docks the mascot directly
      // beside the card, so face it toward the card instead of the dock's
      // (now meaningless) target direction. Reveal-modal and CSS-fallback
      // cases keep the choreography's own flip.
      if (centerFallbackLayout) {
        return resolveFlipStyle(
          choreography.dockPosition,
          centerFallbackLayout.mascotFaces,
          choreography.preferredPose,
        );
      }
      return choreography.flipStyle;
    }

    const mascotSize = 128;
    const mascotLeft = typeof effectiveLayout.mascotStyle.left === 'number' ? effectiveLayout.mascotStyle.left : 0;
    const mascotCenterX = mascotLeft + mascotSize / 2;
    const targetCenterX = spotlightRect.left + spotlightRect.width / 2;
    const targetIsToTheRight = targetCenterX > mascotCenterX;
    const natural = getPoseNaturalDirection(choreography.preferredPose);

    // The special left-right (dual pointing) pose is already oriented for both
    // directions, so never flip it.
    const preferredLower = (choreography.preferredPose ?? '').toLowerCase();
    if (preferredLower === 'left-right' || preferredLower === 'left_right') {
      return undefined;
    }

    // Default/unlabelled poses are assumed to naturally face right.
    const naturallyPointsLeft = natural === 'left';
    const naturallyPointsRight = natural === null || natural === 'right';

    if (targetIsToTheRight && naturallyPointsLeft) {
      return { transform: 'scaleX(-1)' };
    }
    if (!targetIsToTheRight && naturallyPointsRight) {
      return { transform: 'scaleX(-1)' };
    }
    return undefined;
  }, [
    spotlightRect,
    effectiveLayout,
    centerFallbackLayout,
    choreography.dockPosition,
    choreography.preferredPose,
    choreography.flipStyle,
  ]);

  const mascotStyle: CSSProperties = {
    ...effectiveLayout?.mascotStyle,
    // CSS-fallback mascot anchored by `bottom-3` — lift it above the
    // client-zone footer strip so it doesn't cover the chrome.
    ...(!effectiveLayout &&
    choreography.mobileCardAnchor !== 'below' &&
    typeof window !== 'undefined'
      ? { bottom: uiViewportHeight() - tourViewportBottom() + 12 }
      : {}),
    ...(choreography.mascotImageStyle || {}),
    transform: [
      effectiveLayout?.mascotStyle?.transform,
      choreography.mascotImageStyle?.transform,
      targetAwareFlipStyle?.transform,
    ]
      .filter(Boolean)
      .join(' ') || undefined,
  };

  // Fetch AI hint when step changes and generate_hint is enabled
  useEffect(() => {
    if (isOpen && engine.viewMode === 'runner' && currentStep?.ai?.generate_hint && tourAI.aiAvailable) {
      tourAI.clearHintCache();
      tourAI.getAdaptiveHint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id, isOpen, engine.viewMode]);

  // Track flow_start when a flow is selected
  useEffect(() => {
    if (isOpen && engine.viewMode === 'runner' && engine.selectedFlowId) {
      trackEvent({
        event_type: 'flow_start',
        flow_id: engine.selectedFlowId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine.selectedFlowId, isOpen, engine.viewMode]);

  // Track step completion analytics
  useEffect(() => {
    if (isVerified && currentStep) {
      trackEvent({
        event_type: 'step_complete',
        flow_id: engine.selectedFlowId ?? undefined,
        step_id: currentStep.id,
        step_index: engine.currentStepIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified, currentStep?.id]);

  // Track hint_shown when AI hint is displayed
  useEffect(() => {
    if (tourAI.currentHint?.text && currentStep?.id && hintShownForStep !== currentStep.id) {
      setHintShownForStep(currentStep.id);
      trackEvent({
        event_type: 'hint_shown',
        flow_id: engine.selectedFlowId ?? undefined,
        step_id: currentStep.id,
        step_index: engine.currentStepIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourAI.currentHint?.text, currentStep?.id, hintShownForStep]);

  useEffect(() => {
    if (!qaQuestionTimestamp || !tourAI.messages?.length) {
      return;
    }

    const userMessage = [...tourAI.messages]
      .reverse()
      .find((m) => m.role === 'user' && new Date(m.timestamp).getTime() >= qaQuestionTimestamp);
    if (!userMessage) {
      return;
    }

    const assistantMessage = [...tourAI.messages]
      .reverse()
      .find(
        (m) =>
          m.role === 'assistant' &&
          new Date(m.timestamp).getTime() >= new Date(userMessage.timestamp).getTime()
      );

    if (assistantMessage?.content) {
      setQaResponse(assistantMessage.content);
    }
  }, [tourAI.messages, qaQuestionTimestamp]);

  const handleAskQuestion = async () => {
    const question = qaInput.trim();
    if (!question || tourAI.qaLoading) return;
    setQaResponse(null);
    setQaQuestionTimestamp(Date.now());
    trackEvent({
      event_type: 'qa_asked',
      flow_id: engine.selectedFlowId ?? undefined,
      step_id: currentStep?.id,
      step_index: engine.currentStepIndex,
    });
    const response = await tourAI.answerTourQuestion(question);
    if (response !== null) {
      setQaInput('');
    }
  };

  const handleNextStep = () => {
    // Track step_skip if advancing without verification on a step that has verification
    if (currentStep?.verification && !isVerified) {
      trackEvent({
        event_type: 'step_skip',
        flow_id: engine.selectedFlowId ?? undefined,
        step_id: currentStep.id,
        step_index: engine.currentStepIndex,
      });
    }
    // Track flow_complete when finishing the last step and mark the flow
    // as completed in persisted progress so the index can show a "Done" badge.
    if (engine.isLastStep && engine.selectedFlowId) {
      trackEvent({
        event_type: 'flow_complete',
        flow_id: engine.selectedFlowId,
      });
      progress.markFlowCompleted(engine.selectedFlowId);
    }
    // If this step revealed a modal (reveal_target), ask the modal host to
    // close it so the next step can re-guide the user back to the dropdown.
    if (currentStep?.reveal_target && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(GUIDED_TOUR_DISMISS_REVEAL_EVENT));
    }
    engine.goToNextStep();
  };

  // Automatically advance to the next step as soon as the current step's
  // verification is satisfied (e.g. user opens the user menu or expands a
  // section). This keeps dropdowns open because the user doesn't have to click
  // the tour's Next button, which would steal focus and close the dropdown.
  //
  // Steps with a `reveal_target` are excluded: those wait for the user to
  // click Next so the tour can stay visible on top of the revealed modal.
  useEffect(() => {
    if (isVerified && currentStep?.verification && !currentStep.reveal_target && !engine.isLastStep && !showIntro) {
      handleNextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified, currentStep?.id, currentStep?.reveal_target, engine.isLastStep, showIntro]);

  if (!isOpen || engine.viewMode !== 'runner' || engine.isMinimized || engine.currentFormat !== 'interactive') {
    return null;
  }

  const flowLabel = engine.selectedObjective
    ? engine.selectedObjective.title
    : engine.selectedFlow?.label ?? 'Tour';

  const poseUrl = currentPoseAsset?.file_url ?? defaultPoseUrl ?? assets[0]?.file_url ?? undefined;

  const editInputCls =
    'w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500';
  const renderEditField = (key: string, label: string, opts?: { rows?: number; placeholder?: string }) => (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </span>
      {opts?.rows ? (
        <textarea
          rows={opts.rows}
          value={editDraft[key] ?? ''}
          onChange={(e) => setEditDraft((d) => ({ ...d, [key]: e.target.value }))}
          placeholder={opts?.placeholder}
          className={editInputCls}
        />
      ) : (
        <input
          type="text"
          value={editDraft[key] ?? ''}
          onChange={(e) => setEditDraft((d) => ({ ...d, [key]: e.target.value }))}
          placeholder={opts?.placeholder}
          className={editInputCls}
        />
      )}
    </label>
  );

  return (
    <>
      <div
        data-tour-runner
        data-tour-step-id={currentStep?.id ?? ''}
        className={cn(
          'pointer-events-auto fixed z-[90] w-[min(92vw,24rem)] transition-all duration-300',
          !effectiveLayout &&
            (choreography.mobileCardAnchor === 'below'
              ? 'bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:right-auto sm:translate-x-0'
              : 'top-20 left-1/2 -translate-x-1/2 sm:left-auto sm:right-auto sm:translate-x-0'),
          !effectiveLayout && (choreography.panelSide === 'left' ? 'sm:left-4' : 'sm:right-4')
        )}
        style={cardChromeStyle}
      >
        {/* The viewport bound must live on the Card itself — the positioned
            wrapper's maxHeight doesn't constrain a block child (no clip), so
            the card would otherwise render right past it. */}
        <Card
          className={cn(
            'flex flex-col border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95',
            // Backstop bound for the CSS-fallback anchor before the inline
            // style lands (and for any path that lacks one).
            choreography.mobileCardAnchor === 'below'
              ? 'max-h-[calc(100dvh-2rem)]'
              : 'max-h-[calc(100dvh-6rem)]'
          )}
          style={cardChromeStyle?.maxHeight ? { maxHeight: cardChromeStyle.maxHeight } : undefined}
        >
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                {showIntro ? 'Guided tour' : (currentStep?.eyebrow ?? flowLabel)}
              </p>
              <h3 className="truncate text-base font-bold text-slate-900 dark:text-slate-100">
                {showIntro ? `Welcome — ${flowLabel}` : (currentStep?.title ?? 'Welcome')}
              </h3>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleTts}
                title={ttsEnabled ? 'Mute tour audio' : 'Unmute tour audio'}
              >
                {ttsEnabled ? (
                  <Volume2 className="h-4 w-4" />
                ) : (
                  <VolumeX className="h-4 w-4" />
                )}
              </Button>
              {/* Inline editor — dev + root only. Edits the step (or the
                  flow's intro text while the welcome card is showing)
                  without leaving the tour, so the author sees the exact
                  mascot/spotlight context the learner sees. */}
              {editAvailable && !captureMode && (showIntro ? !!engine.selectedFlow : !!currentStep) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', editMode && 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300')}
                  onClick={() =>
                    editMode
                      ? setEditMode(null)
                      : showIntro
                        ? openFlowEditor()
                        : openStepEditor()
                  }
                  title={editMode ? 'Close editor' : showIntro ? 'Edit tour intro text' : 'Edit this step'}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={engine.minimize}
                title="Minimize tour"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          {/* min-h-0 (not a fixed max-h) lets the content shrink to whatever
              the Card's viewport-bound max-height leaves under the header —
              the overflow scrolls inside the card. */}
          <CardContent className="min-h-0 space-y-4 overflow-y-auto pt-0">
            {editMode ? (
              <div className="space-y-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                  Editing {editMode === 'step' ? `step "${currentStep?.id}"` : `intro for "${engine.selectedFlow?.id}"`}
                </p>
                {editMode === 'step' ? (
                  <>
                    {renderEditField('eyebrow', 'Eyebrow')}
                    {renderEditField('title', 'Title')}
                    {renderEditField('summary', 'On-card text', { rows: 2 })}
                    {renderEditField('scripted_description', 'Narration (what the voice reads)', { rows: 5 })}
                    {renderEditField('bullets', 'Bullets — one per line', { rows: 3 })}
                    {renderEditField('focus_target', 'Spotlight selector (data-tour-id)', { placeholder: 'e.g. payroll-tab' })}
                    {renderEditField('tts_voice', 'Voice', { placeholder: 'e.g. af_heart' })}

                    {/* Mascot — pose picked visually from the loaded assets;
                        the live mascot on the page previews the choice
                        immediately (edit mode feeds the draft into the
                        choreography hook). 'Auto' clears the field so the
                        choreography algorithm picks by dock side. */}
                    <div>
                      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                        Mascot pose
                      </span>
                      <div className="grid grid-cols-5 gap-1">
                        <button
                          type="button"
                          onClick={() => setEditDraft((d) => ({ ...d, mascot_pose: '' }))}
                          className={cn(
                            'flex h-14 flex-col items-center justify-center rounded-md border text-[9px] font-medium transition-colors',
                            !editDraft.mascot_pose
                              ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/40 dark:text-indigo-300'
                              : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:hover:border-slate-500 dark:hover:bg-slate-900/60'
                          )}
                        >
                          <Sparkles className="mb-0.5 h-4 w-4" />
                          Auto
                        </button>
                        {assets
                          .filter((a) => a.file_url && a.asset_key)
                          .map((a) => (
                            <button
                              key={a.asset_key}
                              type="button"
                              title={a.display_name ?? a.asset_key ?? ''}
                              onClick={() => setEditDraft((d) => ({ ...d, mascot_pose: a.asset_key ?? '' }))}
                              className={cn(
                                'flex h-14 flex-col items-center justify-center overflow-hidden rounded-md border p-1 transition-colors',
                                editDraft.mascot_pose === a.asset_key
                                  ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/40'
                                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:hover:border-slate-500 dark:hover:bg-slate-900/60'
                              )}
                            >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={a.file_url!} alt={a.display_name ?? ''} className="h-8 w-8 object-contain" />
                              <span className="mt-0.5 w-full truncate text-center text-[8px] text-slate-500 dark:text-slate-400">
                                {a.asset_key}
                              </span>
                            </button>
                          ))}
                      </div>
                      {!!editDraft.mascot_pose &&
                        !assets.some((a) => a.asset_key === editDraft.mascot_pose) && (
                          <button
                            type="button"
                            title="Not a loaded asset — click to clear back to auto"
                            onClick={() => setEditDraft((d) => ({ ...d, mascot_pose: '' }))}
                            className="mt-1 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                          >
                            custom: {editDraft.mascot_pose} <X className="h-2.5 w-2.5" />
                          </button>
                        )}
                    </div>

                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                        Mascot position
                      </span>
                      <select
                        value={editDraft.mascot_position ?? ''}
                        onChange={(e) => setEditDraft((d) => ({ ...d, mascot_position: e.target.value }))}
                        className={editInputCls}
                      >
                        <option value="">Auto (choreography sequence)</option>
                        <option value="left">Left edge</option>
                        <option value="right">Right edge</option>
                        <option value="top">Top edge</option>
                        <option value="bottom">Bottom edge</option>
                        <option value="top-left">Top-left corner</option>
                        <option value="top-right">Top-right corner</option>
                        <option value="bottom-left">Bottom-left corner</option>
                        <option value="bottom-right">Bottom-right corner</option>
                        <option value="left-center">Left-center</option>
                        <option value="right-center">Right-center</option>
                        <option value="top-center">Top-center</option>
                        <option value="bottom-center">Bottom-center</option>
                      </select>
                    </label>
                  </>
                ) : (
                  <>
                    {renderEditField('label', 'Tour name')}
                    {renderEditField('view_summary', 'Welcome card text (also the intro narration)', { rows: 4 })}
                    {renderEditField('summary', 'Summary (tour index card)', { rows: 2 })}
                    {renderEditField('objectives', 'Learning objectives — one per line', { rows: 3 })}
                  </>
                )}
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
                    Writes to the module YAML. &quot;Save + audio&quot; re-makes only
                    {editMode === 'step' ? " this step's" : ' the intro'} voice
                    file — nothing else is touched.
                  </p>
                  {(ttsIsPlaying || ttsIsLoading) && (
                    <button
                      type="button"
                      onClick={ttsStop}
                      className="shrink-0 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700"
                    >
                      <VolumeX className="mr-1 inline h-3 w-3" />
                      Stop voice
                    </button>
                  )}
                </div>
              </div>
            ) : showIntro && engine.selectedFlow ? (
              <>
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                  {engine.selectedFlow.view_summary || engine.selectedFlow.summary}
                </p>

                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    By the end of this tour you&apos;ll be able to
                  </p>
                  <ul className="space-y-1.5">
                    {(engine.selectedFlow.objectives?.length
                      ? engine.selectedFlow.objectives
                      : engine.selectedFlow.steps.slice(0, 5).map((s) => s.title)
                    ).map((objective, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500 dark:bg-indigo-400" />
                        {objective}
                      </li>
                    ))}
                    {!engine.selectedFlow.objectives?.length && engine.selectedFlow.steps.length > 5 && (
                      <li className="text-xs text-slate-500 dark:text-slate-400">
                        …and {engine.selectedFlow.steps.length - 5} more
                      </li>
                    )}
                  </ul>
                </div>

                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  Ready when you are — press Start to get going.
                </p>
              </>
            ) : /* When the target element is missing, show a helpful message.
                This happens when:
                - The user closes the modal mid-tour (reveal_target steps)
                - The user navigates away from the expected tab (regular steps)
                In both cases, guide the user back. */
            focusTargetState === 'missing' && currentStep?.focus_target ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2 dark:border-amber-900/50 dark:bg-amber-900/20">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {currentStep.reveal_target
                    ? 'The document editor was closed'
                    : 'This section is not visible'}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Click <strong>Back</strong> to return to the previous step and try again, or{' '}
                  <strong>Next</strong> to skip this step.
                </p>
                {/* Dev+root audit aid: name the missing selector so the author
                    can fix focus_target (or add the data-tour-id) without
                    digging through YAML, and jump straight into the editor. */}
                {editAvailable && !captureMode && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400">
                    Missing: <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/50">[data-tour-id=&quot;{currentStep.focus_target}&quot;]</code>
                    {currentStep.target_tab && (
                      <> on tab <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/50">{currentStep.target_tab}</code></>
                    )}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {currentStep.target_tab && (
                    <button
                      type="button"
                      onClick={() => engine.goToStepById(currentStep.id)}
                      className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60"
                    >
                      Take me there
                    </button>
                  )}
                  {/* Sandbox steps point at controls that only exist on the
                      real-client header — if they're missing, the direct fix
                      is entering the sandbox, which reloads and resumes the
                      tour via the pending-open stash. */}
                  {(currentStep.focus_target.includes('sandbox') ||
                    currentStep.verification?.target_selector?.includes('sandbox')) && (
                    <button
                      type="button"
                      onClick={() =>
                        void enterTrainingSandbox({
                          moduleKey,
                          resume: { objectiveId: selectedObjective?.id },
                        })
                      }
                      className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60"
                    >
                      Enter Sandbox
                    </button>
                  )}
                  {editAvailable && !captureMode && (
                    <button
                      type="button"
                      onClick={openStepEditor}
                      className="rounded-md border border-indigo-300 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 dark:hover:bg-indigo-900/60"
                    >
                      Edit step
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
            {(currentStep?.scripted_description || currentStep?.summary) && (
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {currentStep.scripted_description || currentStep.summary}
              </p>
            )}

            {currentStep?.bullets && currentStep.bullets.length > 0 && (
              <ul className="space-y-1.5">
                {currentStep.bullets.map((bullet, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500 dark:bg-indigo-400" />
                    {bullet}
                  </li>
                ))}
              </ul>
            )}

            {currentStep?.verification && !isVerified && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
                <p className="font-medium">{currentStep.verification.pending_message}</p>
              </div>
            )}

            <StepHintGenerator
              hint={tourAI.currentHint?.text ?? null}
              loading={tourAI.hintLoading}
              aiAvailable={tourAI.aiAvailable}
              hintEnabled={!!currentStep?.ai?.generate_hint}
            />

            {showQAPanel && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2 dark:border-indigo-800 dark:bg-indigo-900/30">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">Ask Total Assistant</span>
                  <button onClick={() => { setShowQAPanel(false); setQaResponse(null); setQaQuestionTimestamp(null); setQaInput(''); }} className="text-indigo-400 hover:text-indigo-600 dark:text-indigo-300 dark:hover:text-indigo-200">
                    <span className="text-xs">✕</span>
                  </button>
                </div>
                {qaResponse && (
                  <p className="text-xs leading-relaxed text-slate-700 bg-white/60 rounded p-2 dark:text-slate-200 dark:bg-slate-900/60">{qaResponse}</p>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={qaInput}
                    onChange={(e) => setQaInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAskQuestion()}
                    placeholder="Ask about this step..."
                    className="min-w-0 flex-1 rounded-md border border-indigo-200 bg-white px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-indigo-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
                    disabled={tourAI.qaLoading}
                  />
                  <Button size="sm" className="h-7 px-2 text-xs" onClick={handleAskQuestion} disabled={tourAI.qaLoading || !qaInput.trim()}>
                    {tourAI.qaLoading ? '...' : 'Ask'}
                  </Button>
                </div>
              </div>
            )}
              </>
            )}

            <div className="flex items-center justify-between pt-2">
              {editMode ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={editSaving}
                    onClick={() => setEditMode(null)}
                  >
                    Cancel
                  </Button>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={editSaving}
                      onClick={() => void saveEdit(false)}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1"
                      disabled={editSaving}
                      onClick={() => void saveEdit(true)}
                      title={
                        editMode === 'step'
                          ? "Save and re-generate just this step's voice audio"
                          : "Save and re-generate just the intro's voice audio"
                      }
                    >
                      {editSaving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Volume2 className="h-3.5 w-3.5" />
                      )}
                      Save + audio
                    </Button>
                  </div>
                </>
              ) : showIntro ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={engine.returnToIndex}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Tours
                  </Button>
                  <div className="flex items-center gap-1">
                  {/* Intro narration control — pause/resume while the summary
                      WAV plays, replay once it ends. */}
                  {ttsEnabled && engine.selectedFlow?.summary_audio_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 px-2"
                      disabled={ttsIsLoading}
                      onClick={
                        ttsIsPaused
                          ? ttsResume
                          : ttsIsPlaying
                            ? ttsPause
                            : () => void ttsPlayUrl(engine.selectedFlow!.summary_audio_url!)
                      }
                      title={
                        ttsIsPaused
                          ? 'Resume voice'
                          : ttsIsPlaying
                            ? 'Pause voice'
                            : 'Play voice'
                      }
                      aria-label={
                        ttsIsPaused
                          ? 'Resume voice'
                          : ttsIsPlaying
                            ? 'Pause voice'
                            : 'Play voice'
                      }
                    >
                      {ttsIsLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : ttsIsPlaying && !ttsIsPaused ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  {editAvailable && !captureMode && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      disabled={audioRegenBusy}
                      onClick={() => void regenCurrentAudio()}
                      title="Regenerate the intro audio (dev only)"
                    >
                      {audioRegenBusy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  <Button size="sm" className="gap-1" onClick={dismissIntro}>
                    <Play className="h-4 w-4" />
                    Start the tour
                  </Button>
                  </div>
                </>
              ) : (
              <>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  disabled={engine.isFirstStep && !engine.selectedObjectiveId}
                  onClick={engine.goToPreviousStep}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
                {tourAI.aiAvailable && !showQAPanel && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-indigo-600 dark:text-indigo-300"
                    onClick={() => setShowQAPanel(true)}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    Ask
                  </Button>
                )}
                {/* Audio control — pause/resume while playing, replay when the
                    narration has ended or hasn't started yet. */}
                {ttsEnabled && narrationText && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 px-2"
                    disabled={ttsIsLoading}
                    onClick={
                      ttsIsPaused ? ttsResume : ttsIsPlaying ? ttsPause : ttsPlay
                    }
                    title={
                      ttsIsPaused
                        ? 'Resume voice'
                        : ttsIsPlaying
                          ? 'Pause voice'
                          : 'Play voice'
                    }
                    aria-label={
                      ttsIsPaused
                        ? 'Resume voice'
                        : ttsIsPlaying
                          ? 'Pause voice'
                          : 'Play voice'
                    }
                  >
                    {ttsIsLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : ttsIsPlaying && !ttsIsPaused ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-1">
              {/* Dev+root: re-synthesize this step's WAVs (narration + title)
                  without editing — BullMQ job, progress in Media manager. */}
              {editAvailable && !captureMode && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                  disabled={audioRegenBusy}
                  onClick={() => void regenCurrentAudio()}
                  title="Regenerate this step's audio (dev only)"
                >
                  {audioRegenBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {engine.currentStepIndex + 1} / {engine.currentSteps.length}
              </div>
              </div>

              <Button
                size="sm"
                className="gap-1"
                disabled={!canAdvance && verificationRequired && focusTargetState !== 'missing'}
                onClick={handleNextStep}
              >
                {engine.isLastStep ? 'Finish' : 'Next'}
                <ChevronRight className="h-4 w-4" />
              </Button>
              </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div
        className={cn(
          'pointer-events-none fixed z-[85] h-20 w-20 transition-all duration-300 sm:block sm:h-32 sm:w-32',
          !effectiveLayout && (choreography.mobileCardAnchor === 'below'
            ? 'top-3 left-1/2 -translate-x-1/2'
            : 'bottom-3 left-1/2 -translate-x-1/2'),
          !effectiveLayout && choreography.dockPosition.includes('right') && 'sm:right-4 sm:left-auto',
          !effectiveLayout && choreography.dockPosition.includes('left') && 'sm:left-4 sm:right-auto',
          !effectiveLayout && choreography.dockPosition.includes('top') && 'sm:top-24 sm:bottom-auto',
          !effectiveLayout && choreography.dockPosition.includes('bottom') && 'sm:bottom-24 sm:top-auto',
          !effectiveLayout && choreography.dockPosition.includes('center') && 'sm:top-1/2 sm:-translate-y-1/2 sm:translate-x-0'
        )}
        style={mascotStyle}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={poseUrl}
          alt="Tour mascot"
          className="h-full w-full object-contain"
          style={choreography.mascotImageStyle}
        />
      </div>
    </>
  );
}
